import PropTypes from 'prop-types'
import { Calendar, Download, Mail, Printer, Wrench } from 'lucide-react'

import { AdasCircularProgress } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { BUTTONS_DISPLAY_LABEL } from '../../constants'

/**
 * CAR-3677 — sticky selection ribbon shared by the Quote and Work Order queues.
 *
 * Consolidates every record-level action into one predictable place that
 * appears whenever rows are selected. The ribbon adapts to how many rows are
 * selected:
 *   - single (count === 1): Create Work Order and New Appointment (quote only), Close, View PDF,
 *     Print, Email, Clear — Print and Create Work Order show only when their handler is passed
 *   - multi  (count  >  1): Close, Clear only (per-record actions are hidden)
 *
 * Renders nothing when nothing is selected.
 *
 * A client user keeps only the actions that read: View PDF and Clear. Email sends on
 * the center's behalf, New Appointment and Close both write, so all three are dropped.
 * One change here covers the Quote, Work Order and Invoice queues alike.
 */
export const QueueSelectionRibbon = ({
  count,
  recordLabelPlural,
  showNewAppointment = false,
  showClose = true,
  closeLabel,
  closeDisabled = false,
  className = '',
  onViewPdf,
  onPrint,
  onEmail,
  onConvertToWorkOrder,
  onNewAppointment,
  onClose,
  onClear,
  pdfLoading = false,
  closeLoading = false,
  convertLoading = false
}) => {
  const isReadOnly = useIsReadOnly()

  if (!count || count < 1) return null

  const isSingle = count === 1
  // Default "Close Quotes / Work Orders / Invoices"; callers can override (e.g.
  // the Invoice queue swaps to "Send to QBO" when QuickBooks is connected).
  const closeText = closeLabel || `Close ${recordLabelPlural}`
  const translucentBtn =
    'inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40'

  const closeButton = showClose && !isReadOnly && (
    <button
      type='button'
      onClick={onClose}
      disabled={closeLoading || closeDisabled}
      className='inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#0A1628] transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40'
    >
      {closeLoading && <AdasCircularProgress color='inherit' size={16} />}
      {closeText}
    </button>
  )

  return (
    <div
      className={`sticky top-0 z-30 mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-[#2C3D4D] px-4 py-3 text-white shadow-sm ${className}`}
    >
      <div className='flex items-center gap-2'>
        <span className='inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-white/20 px-2 text-[13px] font-bold'>
          {count}
        </span>
        <span className='text-[14px] font-medium'>selected</span>
      </div>

      <div className='ml-auto flex flex-wrap items-center gap-2'>
        {/* Single selection: the record's actions, write actions first. Several selected: Close only. */}
        {isSingle ? (
          <>
            {onConvertToWorkOrder && !isReadOnly && (
              <button type='button' onClick={onConvertToWorkOrder} disabled={convertLoading} className={translucentBtn}>
                {convertLoading ? <AdasCircularProgress color='inherit' size={16} /> : <Wrench className='h-4 w-4' />}
                Create Work Order
              </button>
            )}
            {/* Booking an appointment is the one write a client user may make. */}
            {showNewAppointment && (
              <button
                type='button'
                onClick={onNewAppointment}
                className='inline-flex items-center gap-2 rounded-lg bg-[#1F6FEB] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1F6FEB]/90'
              >
                <Calendar className='h-4 w-4' />
                {BUTTONS_DISPLAY_LABEL.NEW_APPOINTMENT}
              </button>
            )}
            {closeButton}
            <button type='button' onClick={onViewPdf} disabled={pdfLoading} className={translucentBtn}>
              {pdfLoading ? <AdasCircularProgress color='inherit' size={16} /> : <Download className='h-4 w-4' />}
              View PDF
            </button>
            {onPrint && (
              <button type='button' onClick={onPrint} disabled={pdfLoading} className={translucentBtn}>
                <Printer className='h-4 w-4' />
                Print
              </button>
            )}
            {!isReadOnly && (
              <button type='button' onClick={onEmail} className={translucentBtn}>
                <Mail className='h-4 w-4' />
                Email
              </button>
            )}
          </>
        ) : (
          closeButton
        )}
        <button
          type='button'
          onClick={onClear}
          className='inline-flex items-center rounded-lg border border-white/40 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10'
        >
          Clear
        </button>
      </div>
    </div>
  )
}

QueueSelectionRibbon.propTypes = {
  count: PropTypes.number.isRequired,
  recordLabelPlural: PropTypes.string.isRequired,
  showNewAppointment: PropTypes.bool,
  showClose: PropTypes.bool,
  closeLabel: PropTypes.string,
  closeDisabled: PropTypes.bool,
  className: PropTypes.string,
  onViewPdf: PropTypes.func,
  onPrint: PropTypes.func,
  onEmail: PropTypes.func,
  onConvertToWorkOrder: PropTypes.func,
  onNewAppointment: PropTypes.func,
  onClose: PropTypes.func,
  onClear: PropTypes.func,
  pdfLoading: PropTypes.bool,
  closeLoading: PropTypes.bool,
  convertLoading: PropTypes.bool
}
